/**
 * Price history (batch 8 T14c, #3036): one panel per requested stream, each with its own request and its own pages.
 *
 * The four streams are kept apart because they are different facts: the catalog sell price, the catalog cost figure
 * (NOT what was paid), supplier list-price changes (each in the currency it was quoted in; no exchange rate is
 * applied, and a supplier part is its own stream), and what was actually paid per receipt layer. An opening price
 * the history cannot give says why (`before_history_start`, `no_history`, `mixed_streams`); it is never a later price
 * carried backwards and never 0. Paying one stream's page never changes another's.
 */
import { useState } from 'react';
import { Alert, LinearProgress, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportErrorKey, useInventoryReport, type PriceKind, type ReportParams, type WirePoint, type WireSeries } from '@/hooks/useInventoryReports';
import { ReportPager } from './Pager';
import { Figure, MetricValue, Named, ReportTable, Unknown, WindowInfoText, useReportDisplay } from './ReportParts';
import { formatExactEuro, formatInstant, formatQuotedAmount } from './reportFormat';
import { changeTypeKey, costStatusKey } from './reportLabels';
import { withPage } from './reportFilters';

export interface PriceHistoryViewProps {
  /** The validated request without `kind`, `limit` and `offset`. */
  base: ReportParams;
  kinds: readonly PriceKind[];
  pageSize: number;
}

export function PriceHistoryView({ base, kinds, pageSize }: PriceHistoryViewProps) {
  return (
    <Stack spacing={3}>
      {kinds.map((kind) => (
        <SeriesPanel key={kind} base={base} kind={kind} pageSize={pageSize} />
      ))}
    </Stack>
  );
}

function SeriesPanel({ base, kind, pageSize }: { base: ReportParams; kind: PriceKind; pageSize: number }) {
  const { t } = useTranslation('inventoryReports');
  const [offset, setOffset] = useState(0);
  const params = withPage({ ...base, kind }, pageSize, offset);
  const query = useInventoryReport('price-history', params);
  const series = query.data?.series.find((s) => s.kind === kind);
  const zone = query.data?.window?.timeZone ?? null;
  return (
    <Stack spacing={1} component="section" aria-label={t(`priceHistory.kind.${kind}`)} aria-busy={query.isFetching}>
      <Typography variant="h6" component="h3">
        {t(`priceHistory.kind.${kind}`)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t(`priceHistory.note.${kind}`)}
      </Typography>
      {query.isFetching && <LinearProgress aria-label={t('state.loading')} />}
      {query.error !== null && query.error !== undefined && <Alert severity="error">{t(reportErrorKey(query.error))}</Alert>}
      {query.isStale && <Alert severity="info">{t('state.stale')}</Alert>}
      {query.data !== undefined && query.data.window !== null && (
        <WindowInfoText>
          {t('window.summary', { from: query.data.window.fromDate, to: query.data.window.toDate, days: query.data.window.calendarDays, zone: query.data.window.timeZone })}
        </WindowInfoText>
      )}
      {series !== undefined && (
        <>
          <SeriesHeader series={series} zone={zone} />
          {series.points.length === 0 && series.pagination.total === 0 ? (
            <Alert severity="info">{t('priceHistory.empty')}</Alert>
          ) : (
            <ReportTable label={t(`priceHistory.kind.${kind}`)}>
              <PointsHead kind={kind} />
              <TableBody>
                {series.points.map((point, index) => (
                  <PointRow key={`${point.kind}-${point.changedAt}-${String(index)}`} point={point} zone={zone} />
                ))}
              </TableBody>
            </ReportTable>
          )}
          <ReportPager pagination={series.pagination} rows={series.points.length} onOffset={setOffset} disabled={query.isFetching} />
        </>
      )}
    </Stack>
  );
}

function SeriesHeader({ series, zone }: { series: WireSeries; zone: string | null }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const amount = (cents4: string, currency?: string) => (currency === undefined ? formatExactEuro(cents4, language) : formatQuotedAmount(cents4, currency, language));
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {series.historyStartsAt === null ? t('priceHistory.noHistory') : t('priceHistory.historyStarts', { at: formatInstant(series.historyStartsAt, zone, language) })}
      </Typography>
      {series.openingPrice !== null && (
        <Typography variant="body2" component="div">
          {t('priceHistory.opening')}:{' '}
          <MetricValue
            metric={series.openingPrice}
            render={(v) => t('priceHistory.openingValue', { amount: amount(v.cents4, v.stream?.currency), at: formatInstant(v.asOfEventAt, zone, language) })}
          />
        </Typography>
      )}
      {series.openingPrices !== null && series.openingPrices.length > 0 && (
        <Stack component="ul" sx={{ m: 0, pl: 3 }}>
          {series.openingPrices.map((o) => (
            <Typography key={`${o.supplierId}-${o.supplierPartId}-${o.currency}`} component="li" variant="body2">
              <Named kind="supplier" id={o.supplierId} />: {t('priceHistory.openingValue', { amount: amount(o.cents4, o.currency), at: formatInstant(o.asOfEventAt, zone, language) })}
            </Typography>
          ))}
        </Stack>
      )}
      {series.streamCount !== null && <Typography variant="caption">{t('priceHistory.streams', { count: series.streamCount })}</Typography>}
      <Typography variant="caption" color="text.secondary" component="div">
        <MetricValue metric={series.history} render={(points) => t('priceHistory.historyPoints', { count: points })} />
      </Typography>
      {series.clockInversions > 0 && (
        <Typography variant="caption" color="warning.main">
          {t('priceHistory.clockInversions', { count: series.clockInversions })}
        </Typography>
      )}
    </Stack>
  );
}

function PointsHead({ kind }: { kind: PriceKind }) {
  const { t } = useTranslation('inventoryReports');
  return (
    <TableHead>
      <TableRow>
        <TableCell>{t('priceHistory.when')}</TableCell>
        {kind === 'paid' ? (
          <>
            <TableCell align="right">{t('priceHistory.quantity')}</TableCell>
            <TableCell align="right">{t('priceHistory.unitCost')}</TableCell>
            <TableCell>{t('priceHistory.warehouse')}</TableCell>
            <TableCell>{t('priceHistory.order')}</TableCell>
            <TableCell>{t('priceHistory.supplier')}</TableCell>
          </>
        ) : (
          <>
            {kind === 'supplier' && <TableCell>{t('priceHistory.supplier')}</TableCell>}
            <TableCell>{t('priceHistory.changeType')}</TableCell>
            <TableCell align="right">{t('priceHistory.oldPrice')}</TableCell>
            <TableCell align="right">{t('priceHistory.newPrice')}</TableCell>
            <TableCell>{t('priceHistory.source')}</TableCell>
          </>
        )}
      </TableRow>
    </TableHead>
  );
}

function PointRow({ point, zone }: { point: WirePoint; zone: string | null }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const when = <TableCell>{formatInstant(point.changedAt, zone, language)}</TableCell>;
  if (point.kind === 'paid') {
    return (
      <TableRow>
        {when}
        <TableCell align="right"><Figure value={point.quantity} /></TableCell>
        <TableCell align="right">
          {point.unitCostCents4 === null ? (
            <>
              <Unknown reason="cost_unconfirmed" />
              <Typography variant="caption" color="text.secondary" component="div">
                {t(costStatusKey(point.costStatus))}
              </Typography>
            </>
          ) : (
            formatExactEuro(point.unitCostCents4, language)
          )}
        </TableCell>
        <TableCell><Named kind="warehouse" id={point.warehouseId} /></TableCell>
        <TableCell>{point.orderNumber ?? t('priceHistory.noOrder')}</TableCell>
        <TableCell>{point.supplierId === null ? '' : <Named kind="supplier" id={point.supplierId} />}</TableCell>
      </TableRow>
    );
  }
  const currency = point.kind === 'supplier' ? point.currency : undefined;
  const show = (cents4: string) => (currency === undefined ? formatExactEuro(cents4, language) : formatQuotedAmount(cents4, currency, language));
  return (
    <TableRow>
      {when}
      {point.kind === 'supplier' && <TableCell><Named kind="supplier" id={point.supplierId} /></TableCell>}
      <TableCell>{t(changeTypeKey(point.changeType))}</TableCell>
      <TableCell align="right">{point.oldCents4 === null ? t('priceHistory.noPrevious') : show(point.oldCents4)}</TableCell>
      <TableCell align="right">{show(point.newCents4)}</TableCell>
      <TableCell>{point.source}</TableCell>
    </TableRow>
  );
}
